import type { E2eChainCoverage } from '@testflow/shared';
import { useTranslation } from 'react-i18next';
import { pickLang } from '../../lib/pick-lang.js';
import { ClipTooltip } from '../tooltip.js';
import { KpiCard } from './kpi-card.js';

// #69 (REP-6): "Tiefe" der E2E-Kettenabdeckung — läuft der komplette Ablauf durch? Eine EIGENE
// Sektion, strukturell an der Simplifications-Sektion (reporting-page.tsx) orientiert, aber
// bewusst NICHT mit REP-3 (Coverage, "Breite") vermischt: REP-3 zählt einen Prozess schon ab
// einem bestandenen Testfall als abgedeckt (ODER), REP-6 verlangt, dass ALLE Mitglieder einer
// Kette bestanden haben (UND). Projektweit-Charakter wie bei den Simplifications hat REP-6 nicht —
// die Kennzahl ist Plan-/Node-gescopt (kommt aus derselben Summary), deshalb gehört sie in den
// Plan-Block-Bereich der Seite, aber als eigenständige Sektion mit eigener Karte und eigener Liste.
export function E2eChainCoverageSection({
  data,
  planName,
}: {
  data: E2eChainCoverage;
  /** Review I5: Bei zwei oder mehr gewaehlten Plaenen zeigte die Sektion die Zahl des ERSTEN Plans
   *  ohne den Plan zu nennen — direkt unter den Bloecken, die per `showHeading` ihren Plannamen
   *  tragen. Die Bindung an summaryQueries[0] ist gewollt (Plan-Scope ist je Plan verschieden), die
   *  Zahl war aber unattribuierbar. Praezedenzfall in derselben Seite: die Prognose haengt
   *  „· <Planname>" an. Undefined = genau ein Plan gewaehlt -> kein Zusatz noetig. */
  // `| undefined` explizit (exactOptionalPropertyTypes) — Repo-Konvention wie `forecast?:
  // ForecastControls | undefined` in reporting-page.tsx.
  planName?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';

  return (
    <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
      {/* Review M8: Der Header traegt NUR noch die Sektions-Ueberschrift (+ ggf. den Plannamen) und
          im rechten Slot eine Zusatzinfo — wie die Schwester-Sektion (Simplifications), die dort
          `simplTotal` setzt. Vorher standen `t('reporting.rep6')` und das Literal `REP-6` hier UND
          direkt darunter noch einmal auf der KpiCard: vier identische Beschriftungen auf zwei
          Zeilen. Die REP-6-Marke bleibt an der Karte, wo alle anderen REP-Marken auch haengen. */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold text-text-primary">
          {t('reporting.rep6')}
          {planName ? <span className="text-text-muted"> · {planName}</span> : null}
        </h2>
        <span className="text-xs text-text-muted">
          {t('reporting.chainGreenOf', { green: data.greenChains, count: data.totalChains })}
        </span>
      </div>

      <KpiCard
        repLabel="REP-6"
        label={t('reporting.chainCardLabel')}
        value={`${data.chainPct}%`}
        sub={t('reporting.chainSub', { count: data.totalChains })}
        hint={t('reporting.chainHint')}
        tone={data.totalChains > 0 && data.chainPct === 100 ? 'pass' : 'default'}
      />

      {data.chains.length === 0 ? (
        // Branch-Review B8: "0 %" mit einem einzigen Leertext sah aus wie "nichts vorhanden" — der
        // Text stand sowohl fuer "es gibt gar keine E2E-Ketten" als auch fuer "jede Kette wurde
        // verworfen, weil sie nur halb im Plan liegt". Das sind zwei verschiedene Handlungen
        // (Ketten anlegen vs. Plan-Scope nachziehen), also zwei Meldungen.
        <div className="mt-3 text-xs text-text-muted">
          {data.skippedChains > 0
            ? t('reporting.chainAllSkipped', { count: data.skippedChains })
            : t('reporting.chainEmpty')}
        </div>
      ) : (
        // Zielschirm 1280x800: eigener overflow-y-auto mit fester max-height statt Body-Scroll.
        <ul className="mt-3 max-h-64 space-y-1.5 overflow-y-auto text-xs">
          {data.chains.map((c) => (
            <li
              key={c.e2e_id}
              className="flex items-center justify-between gap-3 border-b border-[var(--border)] py-1.5 last:border-b-0"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10.5px] text-text-muted">{c.human_id}</span>
                  {/* Review I4: `||`, nicht `??`. `e2e_processes.name_en` ist NOT NULL DEFAULT ''
                      — eine unuebersetzte Kette traegt den LEEREN STRING, den `??` nicht abfaengt;
                      die englische Ansicht zeigte dann eine Kette ganz ohne Namen. `||` ist auch
                      die Repo-Konvention (signoff-page.tsx, catalog/e2e-tab.tsx). */}
                  {/* #95: Zeile hat kein eigenes Klickziel (reine Report-Liste) — kein
                      fokussierbares Kind zum Umschliessen. tabIndex macht den Namen selbst
                      zum Fokus-Ziel fuer die ClipTooltip-Blase. */}
                  <ClipTooltip text={pickLang(lang, c.name_de, c.name_en)} className="min-w-0">
                    {/* biome-ignore lint/a11y/noNoninteractiveTabindex: reiner Lesetext ohne
                        Klickziel — der Tab-Stop existiert nur fuer die ClipTooltip-Blase (#95). */}
                    <span data-clip tabIndex={0} className="block truncate text-text-body">
                      {pickLang(lang, c.name_de, c.name_en)}
                    </span>
                  </ClipTooltip>
                </div>
                {!c.is_green && c.breaks_at !== null ? (
                  <div className="mt-0.5 text-status-fail">
                    {t('reporting.chainBreaksAt', { n: c.breaks_at, title: c.breaks_at_title })}
                  </div>
                ) : null}
              </div>
              <span
                className={`shrink-0 font-bold tabular-nums ${
                  c.is_green ? 'text-status-pass' : 'text-text-primary'
                }`}
              >
                {c.passed_count}/{c.member_count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
